<!-- A sidebar answer's parts (assets/years/<year>/sidebar.json): text, a link on this site, another
     site's (either in italics with isItalic: an organisation in a highlight's bio), #register, an email
     ({email}: Email.vue, its address encoded; without text, the address itself), another FAQ answer
     ({faq}: opened in place), words in italics ({i}: VIZBI <i>Online</i>), the years since a year,
     this year ({yearsSince}), a price at the visitor's Regional Rate ({regionalPrice}), parts shown
     only to a visitor not in a low-income country ({unlessLowIncome}), parts shown only while the
     Early-bird lasts ({whileEarlyBird}), its rate or deadline ({earlyBird: "percent" or "endText":
     useEarlyBird.js, from the fullstack's keyDates.json), or an online event's dates
     in the visitor's time zone ({date}, as the page shows them); text or a link's may hold {videos}
     and {hours}, the whole library's totals (every video, free ones too). -->
<template>
  <template v-for="(part, index) in parts" :key="index">
    <template v-if="typeof part === 'string'">{{ getText(part) }}</template>
    <i v-else-if="part.i">{{ part.i }}</i>
    <template v-else-if="part.date">{{ eventDates[part.date] }}</template>
    <template v-else-if="part.earlyBird">{{ earlyBird[part.earlyBird] }}</template>
    <template v-else-if="part.yearsSince">{{ thisYear - part.yearsSince }}</template>
    <template v-else-if="part.regionalPrice">{{ getRegionalText(part.regionalPrice) }}</template>
    <router-link v-else-if="part.to && part.isItalic" :to="part.to"
    ><i>{{ part.text }}</i></router-link>
    <router-link v-else-if="part.to" :to="part.to">{{ getText(part.text) }}</router-link>
    <Email v-else-if="part.email && part.text" :to="part.email">{{ part.text }}</Email>
    <Email v-else-if="part.email" :to="part.email" />
    <router-link
      v-else-if="part.faq"
      :to="{path: $route.path, hash: getPanelHash('faq', part.faq)}">{{ part.text }}</router-link>
    <a v-else-if="part.href && part.isItalic" :href="part.href"><i>{{ part.text }}</i></a>
    <a v-else-if="part.href" :href="part.href">{{ getText(part.text) }}</a>
    <a
      v-else-if="part.register"
      :href="registrationUrl()"
      @click.prevent="redirectToRegistration()">{{ part.text }}</a>
    <SidebarText
      v-else-if="part.unlessLowIncome && isLowIncome === false"
      :parts="part.unlessLowIncome" />
    <SidebarText
      v-else-if="part.whileEarlyBird && earlyBird.isEarlyBird"
      :parts="part.whileEarlyBird" />
  </template>
</template>

<script setup>
import {reactive, ref} from "vue";
import Email from "@/components/Email.vue";
import useLibraryTotals from "@/composables/useLibraryTotals";
import {fetchIsLowIncome, fetchRegionalMultiplier} from "@/composables/useRegionalDiscount";
import useLocalEventDates from "@/composables/useLocalEventDates";
import useEarlyBird from "@/composables/useEarlyBird";
import useYearSidebar from "@/composables/useYearSidebar";
import {registrationUrl, redirectToRegistration} from "@/composables/useRedirectToRegistration";
const props = defineProps({parts: {type: Array, required: true}});
const FALLBACK = {videos: "…", hours: "…"}; // until the totals load (moments)
const {stats} = useLibraryTotals();
// another answer's hash, Highlights' state kept (useYearSidebar.js)
const {getPanelHash} = useYearSidebar();
// the online events' dates where the visitor is ("3 November" in London, "4 November" in Sydney)
const {masterclass, workshop} = useLocalEventDates();
const eventDates = reactive({masterclass, workshop});
// the Early-bird: while it lasts, its rate and deadline on the visitor's calendar ("13 November")
const earlyBird = reactive(useEarlyBird());
// a low-income country's visitor: looked up once (cached), only for parts that need it; hidden
// until known (null)
const isLowIncome = ref(null);
async function fetchRegion() {
  isLowIncome.value = await fetchIsLowIncome();
}
if (props.parts.some((part) => part.unlessLowIncome)) fetchRegion();
const thisYear = new Date().getFullYear(); // {yearsSince}: 2026 − 2010 = 16, in 2027 17
// a price at the visitor's Regional Rate ({regionalPrice: 50} → "$50", "$30", "$10", "$3"),
// rounded as #register rounds it (feeRules.js getRegionalPrice); "…" until the lookup answers
const multiplier = ref(null);
async function fetchMultiplier() {
  multiplier.value = await fetchRegionalMultiplier();
}
if (props.parts.some((part) => part.regionalPrice)) fetchMultiplier();
function getRegionalText(price) {
  if (multiplier.value === null) return "…";
  return `$${Math.round(price * multiplier.value)}`;
}
// {videos}, {hours} → the library's totals; {both.videos}, {masterclass.hours}… → that
// collection's (stats.json; the Masterclass language guide's counts, 2026-10-27)
const PLACEHOLDER = /\{(?:(\w+)\.)?(videos|hours)\}/g;
const getText = (text) => text.replace(PLACEHOLDER, (match, from, key) => {
  return stats.value?.[from || "library"]?.[key] ?? (from ? "…" : FALLBACK[key]);
});
</script>
